/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import AppIcon from '@atlaskit/icon/core/app';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports
import {
	BUILTIN_TOOLBAR_KEYS,
	createEditorToolbarActions,
	type EditorToolbarHandlerContext,
	type ManifestEditorToolbarActions,
	type NativeEmbedParameterValues,
} from '@atlaskit/native-embeds-common';
import type { BuiltinToolbarKey } from '@atlaskit/native-embeds-common/types';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { getGlobalTheme } from '@atlaskit/tokens/get-global-theme';
import type { AIPlugin } from '@atlassian/editor-plugin-ai/aiPluginType';
// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports
import { allNativeEmbedCoreFeatures } from '@atlassian/native-embeds-core';
import LoadingOverlay from '@atlassian/native-embeds-core/loading-overlay';
// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
	type NativeEmbedFeature,
	type ResolvedExperienceMeta,
	type ShouldFocusOnClickOptions,
	type TransformUrlOptions,
} from '@atlassian/native-embeds-core/types';

export const MAUI_EXPERIENCE_MANIFEST_ID: ExperienceManifestId = experienceManifestId('maui');

const MAUI_DEFAULT_WIDTH_KILLSWITCH = 'native_embeds_maui_default_width_killswitch';

const isMauiUrl = (url: string): boolean => /\/remix\/ui\/[A-Za-z0-9_-]+/.test(url);

/**
 * Rollout control for commenting on MAUI charts and visualisations.
 *
 * Fires the experiment exposure: the editor only evaluates the toolbar actions
 * and view-mode items for toolbars that show Comment. Confluence's renderer check
 * (`isExtensionCommentsExperienceEnabled`) reads the same experiment; keep them
 * in sync.
 *
 * Fails closed, so a Statsig client that has not initialised yet leaves the
 * Comment action out of the toolbar rather than showing an action that cannot
 * anchor a thread.
 */
const isCommentsOnChartsEnabled = (): boolean => {
	try {
		return expVal('cc_maui_comments_on_charts_exp', 'isEnabled', false);
	} catch {
		return false;
	}
};

/**
 * Experience manifest for a MAUI embed.
 * Register with getNativeEmbedFacade().registerExperienceManifest(MauiExperienceManifest),
 * then call getNativeEmbedFacade().createExperienceForUrl(url) to obtain an Experience for EmbedFrameUI.
 */
export const MauiExperienceManifest: {
	readonly allowMaxWidth: true;
	readonly comments: {
		readonly enabled: true;
	};
	readonly handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	/** Function form — evaluated at runtime so `cc_maui_resize` can resolve after Statsig init. */
	readonly lockResizeAspectRatio: () => boolean;
	/**
	 * MAUI apps stay usable at very small widths, so opt into a much smaller
	 * narrow-fallback threshold than the platform default (480). Below this the
	 * embed collapses to a SmartCard block card in view mode.
	 */
	readonly narrowEmbedThreshold: 50;
	readonly parameterDefaults: {
		readonly width: 760 | undefined;
	};
	// remove this when MAUI is onboarded to the Linking Platform. This is here to allow testing before it is onboarded.
	readonly resolveViaLinkingPlatform: false;
	readonly shouldFocusOnClick: (
		url: string,
		meta: ResolvedExperienceMeta,
		options?: ShouldFocusOnClickOptions,
	) => boolean;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (
		url: string,
		isFullScreen?: boolean,
		options?: TransformUrlOptions,
	) => string;
	readonly uiConfig: {
		readonly appearance: 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: {
			readonly onAskRovoClick: (
				_parameters: NativeEmbedParameterValues,
				context?: EditorToolbarHandlerContext,
			) => void;
		};
		readonly fullScreenDialogControls: {
			readonly hideOpenInNewTab: true;
			readonly hideTitleIcon: true;
		};
		readonly headerControls: {
			readonly hideMoreMenu: true;
			readonly hideTitle: true;
		};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly showBorder: false;
		readonly showExpandButton: false;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
		readonly trackViewed: true;
		readonly viewModeEditorToolbarItems: () => BuiltinToolbarKey[];
		readonly waitForContentReady: boolean;
	};
} = {
	allowMaxWidth: true,
	comments: {
		enabled: true,
	},
	id: MAUI_EXPERIENCE_MANIFEST_ID,
	handleChildCommands: (_experience: Experience, _cmd: NativeEmbedChildCommand): void => {},
	isUrlMatch: isMauiUrl,
	supportedFeatures: new Set<NativeEmbedFeature>(allNativeEmbedCoreFeatures),
	isActive: (): true => true,
	lockResizeAspectRatio: () => !fg('cc_maui_resize'),
	narrowEmbedThreshold: 50,
	parameterDefaults: {
		get width(): 760 | undefined {
			return fg(MAUI_DEFAULT_WIDTH_KILLSWITCH) ? 760 : undefined;
		},
	},
	shouldFocusOnClick: (
		_url: string,
		_meta: ResolvedExperienceMeta,
		options?: ShouldFocusOnClickOptions,
	): boolean => !isExperimentEnabled('cc_maui_editing_experience') || options?.isSelected !== true,
	transformUrl: (url: string, _isFullScreen?: boolean, options?: TransformUrlOptions): string => {
		const urlObj = new URL(url);
		const { colorMode } = getGlobalTheme();

		if (colorMode) {
			urlObj.searchParams.set('theme', colorMode);
		}

		// When the host Confluence page is in edit mode, forward an
		// `enable-edits=` query param (empty value) so the embedded MAUI app
		// can render its edit-only affordances.
		if (options?.isEditMode) {
			urlObj.searchParams.set('enable-edits', '');
		}

		return urlObj.toString();
	},
	// remove this when MAUI is onboarded to the Linking Platform. This is here to allow testing before it is onboarded.
	resolveViaLinkingPlatform: false,
	uiConfig: {
		trackViewed: true,
		// Holds core's loading state until the app reports its first painted frame; the iframe's
		// `load` event fires while the app is still starting. The reveal in `editor-common` waits on
		// that signal. A getter, because the manifest is a module constant and the gate is read per
		// render. Applies to every MAUI embed; an app that never sends the signal keeps its overlay
		// until core's 10s timeout.
		get waitForContentReady() {
			return fg('aifc_page_create_defer_generated_visuals');
		},
		showBorder: false,
		showExpandButton: false,
		renderLoadingComponent: (props: LoadingComponentProps): JSX.Element => {
			return (
				<LoadingOverlay
					variant="simple-bar"
					animationDisabled={props.animationDisabled}
					showBlanket={props.source === 'ai-edit'}
					testId={props.testId}
				/>
			);
		},
		editorToolbarActions: () => {
			const showComment = fg('cc_maui_annotations_on_extensions') && isCommentsOnChartsEnabled();
			return createEditorToolbarActions({
				customActions: {},
				items: [
					...(fg('cc-maui-phase-2')
						? fg('platform_editor_ai_remix_toolbar')
							? [
									BUILTIN_TOOLBAR_KEYS.ASK_ROVO,
									BUILTIN_TOOLBAR_KEYS.REMIX_VARIANT,
									BUILTIN_TOOLBAR_KEYS.SEPARATOR,
								]
							: [BUILTIN_TOOLBAR_KEYS.ASK_ROVO, BUILTIN_TOOLBAR_KEYS.SEPARATOR]
						: []),
					...(expValEquals('cc-maui-experiment', 'isEnabled', true)
						? fg('cc-maui-toolbar-separators-update-jira')
							? [BUILTIN_TOOLBAR_KEYS.BORDER]
							: [BUILTIN_TOOLBAR_KEYS.BORDER, BUILTIN_TOOLBAR_KEYS.SEPARATOR]
						: []),
					BUILTIN_TOOLBAR_KEYS.ALIGNMENT,
					...(fg('cc-maui-toolbar-separators-update-jira') ? [] : [BUILTIN_TOOLBAR_KEYS.SEPARATOR]),
					BUILTIN_TOOLBAR_KEYS.EXPAND,
					// With Comment, match the media toolbar: Copy and Delete move into the
					// "More options" menu, which follows Comment with no separator.
					...(showComment
						? [BUILTIN_TOOLBAR_KEYS.COMMENT]
						: [BUILTIN_TOOLBAR_KEYS.COPY, BUILTIN_TOOLBAR_KEYS.DELETE]),
				],
				moreItems: showComment ? [BUILTIN_TOOLBAR_KEYS.COPY, BUILTIN_TOOLBAR_KEYS.DELETE] : [],
				// Size the menu like media's, for MAUI charts only.
				compactMoreItems: showComment,
			});
		},
		editorToolbarHandlers: {
			onAskRovoClick: (
				_parameters: NativeEmbedParameterValues,
				context?: EditorToolbarHandlerContext,
			): void => {
				const editorApi = context?.editorApi as PublicPluginAPI<[AIPlugin]>;
				// Stage the embed as the inline-Rovo streaming target. The overlay
				// is NOT triggered here — `RovoPromptScreenWithLogic.onSubmit`
				// promotes this on prompt submit. `expValEquals` (NOT NoExposure)
				// because this toolbar click is the designated `cc-maui-experiment`
				// exposure point.
				if (
					// eslint-disable-next-line @atlaskit/platform/no-preconditioning -- gate + experiment layering for MAUI rollout (cc-maui-experiment + cc-maui-phase-2); will simplify post-rollout
					expValEquals('cc-maui-experiment', 'isEnabled', true) &&
					fg('cc-maui-phase-2')
				) {
					const localId = context?.selectedNodeLocalId;
					if (localId) {
						editorApi?.aiExperience?.actions?.stageInlineRovoStreaming?.(localId);
					}
				}
				editorApi?.aiExperience?.actions?.openAIModal?.({
					invokedFrom: 'floatingToolbar',
				});
			},
		},
		fullScreenDialogControls: {
			hideOpenInNewTab: true,
			hideTitleIcon: true,
		},
		headerControls: {
			hideMoreMenu: true,
			hideTitle: true,
		},
		titleIcon: AppIcon,
		appearance: 'overlay',
		// Comment also shows when viewing a live page, under the same rollout as edit mode.
		viewModeEditorToolbarItems: (): BuiltinToolbarKey[] =>
			fg('cc_maui_annotations_on_extensions') && isCommentsOnChartsEnabled()
				? [BUILTIN_TOOLBAR_KEYS.COMMENT]
				: [],
	},
} as const satisfies ExperienceManifest;
